// 사이드바의 순서와 배지 — 순수 계산이다. 채널이 스무 개를 넘으면 순서가 곧 기능이다.

import type { ChannelId } from '@maru/protocol'
import { msg } from './i18n.ts'

export type Notify = 'all' | 'mentions' | 'none'

export interface Listed {
  id: ChannelId
  name: string
  unread: number
  mention_count: number
  notify?: Notify
  favorite?: boolean
  section?: string | null
  /** 대화(DM·그룹 DM). 사람이 칸을 정하지 않았으면 '다이렉트 메시지' 칸에 모인다. */
  dm?: boolean
}

/** 사이드바 한 칸. `title` 이 `null` 이면 머리글 없이 바로 줄들이다. */
export interface Section<T> {
  title: string | null
  channels: readonly T[]
  /** 사용자가 지은 칸인가. 접기·끌어 놓기는 이런 칸에만 있다. */
  custom?: boolean
  emoji?: string | null
  collapsed?: boolean
}

/** 사용자 정의 칸의 사정 — 서버 `prefs::SidebarSection` 과 같은 모양이다. */
export interface SectionSpec {
  name: string
  emoji?: string | null
  collapsed?: boolean
}

/** 기본 칸 이름은 원문이다 — 그리는 자리(`SidebarGroup`)가 번역한다. 사용자가 지은 칸은 그대로. */
const STARRED = msg('즐겨찾기')

/**
 * 즐겨찾기가 맨 위, 그다음 사용자가 지은 칸들(정한 순서, 모르는 칸은 가나다), 그다음 나머지 채널,
 * 마지막이 칸을 정하지 않은 대화.
 * 정한 칸은 **비어 있어도 나온다** — 빈 칸이 사라지면 거기로 끌어다 놓을 수 없다.
 *
 * **즐겨찾기가 칸보다 세다.** 둘 다면 위쪽에만 나온다 — 한 채널이 두 번
 * 보이면 안읽음을 두 번 세게 되고, 사람은 그걸 버그로 읽는다.
 */
export function group_channels<T extends Listed>(
  channels: readonly T[],
  specs: readonly SectionSpec[] = [],
): Section<T>[] {
  const out: Section<T>[] = []
  const starred = channels.filter((c) => c.favorite === true)
  if (starred.length > 0) out.push({ title: STARRED, channels: starred })

  const rest = channels.filter((c) => c.favorite !== true)
  const named = new Map<string, T[]>()
  const loose: T[] = []
  for (const c of rest) {
    const section = c.section ?? null
    if (section === null || section.length === 0) {
      loose.push(c)
      continue
    }
    const bucket = named.get(section)
    if (bucket === undefined) named.set(section, [c])
    else bucket.push(c)
  }
  for (const spec of specs) {
    out.push({
      title: spec.name,
      channels: named.get(spec.name) ?? [],
      custom: true,
      emoji: spec.emoji ?? null,
      collapsed: spec.collapsed === true,
    })
  }
  const known = new Set(specs.map((s) => s.name))
  for (const title of [...named.keys()]
    .filter((t) => !known.has(t))
    .sort((a, b) => a.localeCompare(b, 'ko'))) {
    out.push({ title, channels: named.get(title) ?? [], custom: true })
  }
  const plain = loose.filter((c) => c.dm !== true)
  const talks = loose.filter((c) => c.dm === true)
  const any_head = out.length > 0 || talks.length > 0
  if (plain.length > 0) out.push({ title: any_head ? msg('채널') : null, channels: plain })
  // 대화는 채널 사이에 섞지 않는다 — `#디자인` 과 `이하늘` 이 한 줄로 서면 어느 것이 방이고
  // 어느 것이 사람인지 이름으로만 가려야 한다.
  if (talks.length > 0) out.push({ title: msg('다이렉트 메시지'), channels: talks })
  return out
}

/**
 * 접힌 칸에서도 보이는 줄들 — 지금 연 채널과 배지가 있는 채널. 접었다고 호출을 숨기면
 * 접기가 음소거가 된다. 펼친 칸은 전부다.
 */
export function visible_rows<T extends Listed>(part: Section<T>, open: ChannelId | null): T[] {
  if (part.collapsed !== true) return [...part.channels]
  return part.channels.filter((c) => {
    const b = badge_of(c)
    return c.id === open || b.unread > 0 || b.mention_count > 0
  })
}

/**
 * 사이드바 보기 — 이 기기의 것(G25). 정렬은 `default`(서버가 준 순서 = 들어간 순서)와 `alpha`(가나다).
 * `unread_only` 는 배지가 있는 줄만 남긴다(지금 연 채널은 남는다 — 보던 곳이 사라지면 길을 잃는다).
 */
export interface SidebarView {
  sort: 'default' | 'alpha'
  unread_only: boolean
}

export const SIDEBAR_VIEW_DEFAULT: SidebarView = { sort: 'default', unread_only: false }

/** 저장된 낱말 줄(`alpha unread`)을 보기로. 모르는 낱말은 버린다. */
export function read_sidebar_view(raw: string | null): SidebarView {
  const words = new Set((raw ?? '').split(' '))
  return { sort: words.has('alpha') ? 'alpha' : 'default', unread_only: words.has('unread') }
}

export function sidebar_view_words(v: SidebarView): string {
  return [v.sort === 'alpha' && 'alpha', v.unread_only && 'unread'].filter(Boolean).join(' ')
}

/** 칸 안의 줄을 보기대로 거르고 늘어놓는다. 음소거한 채널은 배지가 없으니 "안 읽은 것만" 에서 빠진다. */
export function arrange_rows<T extends Listed>(
  rows: readonly T[],
  view: SidebarView,
  open: ChannelId | null,
): T[] {
  const kept = view.unread_only
    ? rows.filter((c) => {
        const b = badge_of(c)
        return c.id === open || b.unread > 0 || b.mention_count > 0
      })
    : [...rows]
  if (view.sort === 'alpha') kept.sort((a, b) => a.name.localeCompare(b.name, 'ko'))
  return kept
}

/**
 * 칸 `from` 을 칸 `before` 의 자리로 옮긴 새 목록(G34 — 사이드바에서 머리를 끌어 놓기). 앞에서 뒤로 옮기면
 * 놓은 칸의 **뒤**에 선다 — 끌어 내린 사람은 그 칸 아래를 가리킨 것이다. 모르는 이름이면 그대로.
 */
export function move_section(
  list: readonly SectionSpec[],
  from: string,
  before: string,
): SectionSpec[] {
  const at = list.findIndex((s) => s.name === from)
  const to = list.findIndex((s) => s.name === before)
  const item = list[at]
  if (at < 0 || to < 0 || at === to || item === undefined) return [...list]
  const next = list.filter((_, i) => i !== at)
  next.splice(to, 0, item)
  return next
}

/** 칸 하나를 `delta` 만큼 옮긴 새 목록. 끝을 넘으면 그대로다. */
export function shift_section(
  list: readonly SectionSpec[],
  index: number,
  delta: number,
): SectionSpec[] {
  const to = index + delta
  const next = [...list]
  const item = next[index]
  if (item === undefined || to < 0 || to >= next.length) return next
  next.splice(index, 1)
  next.splice(to, 0, item)
  return next
}

/**
 * 이 채널에 적을 배지.
 *
 * - `none`(음소거)은 **숫자가 하나도 안 뜬다.** 음소거인데 숫자가 뜨면 음소거가 아니다.
 * - `mentions` 는 안읽음 숫자를 지우고 멘션만 남긴다. 나를 부른 것까지 감추면 그건 음소거다.
 */
export function badge_of(c: Listed): { unread: number; mention_count: number } {
  const notify = c.notify ?? 'all'
  if (notify === 'none') return { unread: 0, mention_count: 0 }
  if (notify === 'mentions') return { unread: 0, mention_count: c.mention_count }
  return { unread: c.unread, mention_count: c.mention_count }
}

/**
 * 닫은 대화방이 지금 숨는가. 닫을 때의 마지막 seq(`upto`)보다 새 글이 오면 다시 보인다 —
 * 서버에 다시 묻지 않고 요약의 `last` 만으로 가른다. 지금 연 방은 숨기지 않는다.
 */
export function hidden_after_close(
  last: number,
  upto: number | undefined,
  is_open: boolean,
): boolean {
  return upto !== undefined && !is_open && last <= upto
}
